import React from 'react';
import { CalendarClock, Flag, Play, RotateCcw, TabletSmartphone } from 'lucide-react';
import { formatClock, formatLocalDate } from '../../../lib/eventWorkspace';

/** "9:00 am" for an instant (`started_at`), read in the event's own zone. */
export function instantClock(iso, timeZone) {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const opts = { hour: 'numeric', minute: '2-digit', hour12: true };
    try { return date.toLocaleTimeString('en-AU', timeZone ? { ...opts, timeZone } : opts).replace(/\s?(AM|PM)/i, (m, ap) => ` ${ap.toLowerCase()}`); } catch { return ''; }
}

/** Whole days from today to the event's own calendar date (never shifted through a zone). */
export function daysUntil(localDate, today = new Date()) {
    if (!localDate) return null;
    const [y, m, d] = String(localDate).split('-').map(Number);
    if (!y || !m || !d) return null;
    const start = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    return Math.round((Date.UTC(y, m - 1, d) - start) / 86400000);
}

const byWho = (at, by, name, zone, verb) => {
    const time = instantClock(at, zone);
    if (by === 'auto') return `${verb} automatically${time ? ` at ${time}` : ''}`;
    return `${verb}${time ? ` ${time}` : ''}${name ? ` by ${name}` : ''}`;
};

/**
 * The status bar above the metrics (1013-29): where the event is (Upcoming, Live, Ended), who moved it
 * there ("by James S." or "automatically"), and its controls. The four lifecycle buttons need `events:update`
 * and are hidden without it (D9); the bar itself is always shown.
 */
export default function GuestsLifecycleBar({
    event, phase, summary, view, canManage, onStart, onEnd, onReopen, onReschedule, onOpenCheckIn,
}) {
    const zone = event?.timezone;
    const date = formatLocalDate(event?.date);
    const times = event?.start_time ? `${formatClock(event.start_time)}${event.end_time ? `–${formatClock(event.end_time)}` : ''}` : '';
    const btn = 'inline-flex h-[34px] items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-[13px] font-medium';
    const secondary = `${btn} border border-stone-200 bg-white text-stone-900 hover:bg-stone-50`;
    const primary = `${btn} bg-stone-900 text-white hover:bg-stone-800`;
    const danger = `${btn} bg-red-700 text-white hover:bg-red-800`;

    let tone = 'border-stone-200 bg-white';
    let dot = '#3B82F6';
    let title; let sub; let buttons;
    if (phase === 'event_day') {
        tone = 'border-red-200 bg-red-50/40';
        dot = '#EF4444';
        title = `Live now · ${byWho(event?.started_at, event?.started_by, event?.started_by_name, zone, 'started')}`;
        // The total is everyone the Check-in view lists (Going plus walk-ins), the same set `attended` counts from.
        const total = Number(summary?.checkin_total ?? summary?.going ?? 0);
        const walk = Number(summary?.walk_ins || 0);
        sub = `${Number(summary?.attended || 0)} of ${total} checked in${walk ? ` · ${walk} walk-in${walk === 1 ? '' : 's'}` : ''}. Going guests stay in Going until they check in or the event ends (on its own at 23:59).`;
        buttons = [
            view !== 'checkin' && <button key="open" type="button" data-testid="guests-open-checkin" className={secondary} onClick={onOpenCheckIn}><TabletSmartphone className="h-4 w-4" aria-hidden="true" />Open check-in</button>,
            canManage && <button key="end" type="button" data-testid="guests-end-event" className={danger} onClick={onEnd}><Flag className="h-4 w-4" aria-hidden="true" />End event</button>,
        ];
    } else if (phase === 'after') {
        tone = 'border-stone-200 bg-stone-50';
        dot = '#A8A29E';
        title = `Ended · ${date}`;
        sub = `${byWho(event?.ended_at, event?.ended_by, event?.ended_by_name, zone, 'Ended')}. Going guests without a check-in moved to No-show when it ended. Someone came late? Reopen check-in or switch them on in Check-in.`;
        buttons = [canManage && <button key="reopen" type="button" data-testid="guests-reopen" className={secondary} onClick={onReopen}><RotateCcw className="h-4 w-4" aria-hidden="true" />Reopen check-in</button>];
    } else {
        const days = daysUntil(event?.date);
        const away = days === null ? '' : days < 0 ? 'Not started yet. ' : days === 0 ? 'Today. ' : days === 1 ? 'Tomorrow. ' : `${days} days away. `;
        title = `Upcoming · ${date}${times ? ` · ${times}` : ''}`;
        sub = `${away}Starts on its own at ${event?.start_time ? formatClock(event.start_time) : 'the start time'} on the day, or start it early. Starting it moves nobody.`;
        buttons = [
            canManage && <button key="resched" type="button" data-testid="guests-reschedule-event" className={secondary} onClick={onReschedule}><CalendarClock className="h-4 w-4" aria-hidden="true" />Reschedule event</button>,
            canManage && <button key="start" type="button" data-testid="guests-start-event" className={primary} onClick={onStart}><Play className="h-4 w-4" aria-hidden="true" />Start event</button>,
        ];
    }
    return (
        <div data-testid="guests-lifecycle" data-phase={phase} className={`flex flex-wrap items-center gap-3 rounded-[10px] border px-3.5 py-3 ${tone}`}>
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: dot, boxShadow: phase === 'event_day' ? '0 0 0 4px #FEE2E2' : undefined }} aria-hidden="true" />
            <div className="flex min-w-[240px] flex-1 flex-col">
                <span className="text-[13.5px] font-semibold text-stone-900" data-testid="guests-lifecycle-title">{title}</span>
                <span className="text-xs text-stone-600" data-testid="guests-lifecycle-sub">{sub}</span>
            </div>
            {buttons.filter(Boolean)}
        </div>
    );
}
